/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'

import { FiChevronDown, FiChevronUp, FiCopy, FiExternalLink, FiX } from 'react-icons/fi'

import { Alert, Button } from '@/components/ui'
import { useAuth } from '@/hooks'

import { ChangeRenderer } from './renderers'

import { actionMeta, entityMeta } from '../constants'
import { displayCompany, displayUser, formatDateTime, formatUtc } from '../format'
import type { AuditDetail, AuditRecord } from '../types'

interface DetailPanelProps {
  /** The list row, known immediately — lets the panel open without waiting for the fetch. */
  record: AuditRecord
  /** The full record with its diff, once loaded. */
  detail?: AuditDetail
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  hasPrev: boolean
  hasNext: boolean
  onClose: () => void
  onPrev: () => void
  onNext: () => void
  onEntityHistory: (record: AuditRecord) => void
  /** The record was opened by link and is not in the loaded list, so prev/next cannot reach it. */
  outsideList?: boolean
}

/**
 * What the red and green tinting means.
 *
 * The value blocks carry no `+`/`−` marker — audited values are frequently signed quantities, and a
 * marker in front of one reads as part of the number — so the colour needs explaining once, in a
 * fixed place, rather than on every row.
 */
const LegendRow: React.FC<{ swatchClass: string; children: React.ReactNode }> = ({ swatchClass, children }) => (
  <li className="flex items-center gap-2">
    <span aria-hidden className={`h-3.5 w-6 shrink-0 rounded-field border ${swatchClass}`} />
    <span className="min-w-0">{children}</span>
  </li>
)

const Legend: React.FC = () => (
  <div className="mt-3 rounded-box border border-base-300 bg-base-100 p-3">
    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-base-content/55">Reading the changes</p>
    <ul className="flex flex-col gap-1.5 text-xs text-base-content/70">
      <LegendRow swatchClass="border-error/40 bg-error/10">Previous value — replaced or removed</LegendRow>
      <LegendRow swatchClass="border-success/40 bg-success/10">New value — added or set</LegendRow>
    </ul>
  </div>
)

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'

/** Keep Tab and Shift+Tab cycling within the dialog instead of escaping to the page behind it. */
const trapFocus = (e: KeyboardEvent, container: HTMLElement | null) => {
  if (!container) return
  const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE))
  if (focusable.length === 0) {
    e.preventDefault()
    return
  }
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement
  const outside = !container.contains(active)
  if (e.shiftKey && (active === first || active === container || outside)) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && (active === last || outside)) {
    e.preventDefault()
    first.focus()
  }
}

const MetaRow: React.FC<{ label: string; children: React.ReactNode; title?: string }> = ({
  label,
  children,
  title,
}) => (
  <div className="grid grid-cols-[64px_1fr] gap-3 py-1 text-sm">
    <dt className="text-right text-base-content/55">{label}</dt>
    <dd className="min-w-0 truncate" title={title}>
      {children}
    </dd>
  </div>
)

export const DetailPanel: React.FC<DetailPanelProps> = ({
  record,
  detail,
  isLoading,
  isError,
  onRetry,
  hasPrev,
  hasNext,
  onClose,
  onPrev,
  onNext,
  onEntityHistory,
  outsideList = false,
}) => {
  const [entered, setEntered] = useState(false)
  const [copied, setCopied] = useState(false)
  const panelRef = useRef<HTMLElement>(null)
  const { hasPermission } = useAuth()
  const canCopyRawJson = hasPermission('staff')

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  // Focus: move into the dialog on open and hand it back on close, so keyboard and screen-reader
  // users land in the panel rather than in the table dimmed behind it. The row that opened it may
  // have been virtualised away by then, hence the `isConnected` check.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    panelRef.current?.focus()
    return () => {
      if (opener?.isConnected) opener.focus()
    }
  }, [])

  // Keyboard: Tab is trapped inside the dialog; Esc closes; ↑/↓ (and k/j) move between records.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (e.key === 'Tab') {
        trapFocus(e, panelRef.current)
        return
      }
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return
      if (e.key === 'Escape') onClose()
      else if ((e.key === 'ArrowUp' || e.key === 'k') && hasPrev) {
        e.preventDefault()
        onPrev()
      } else if ((e.key === 'ArrowDown' || e.key === 'j') && hasNext) {
        e.preventDefault()
        onNext()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, onPrev, onNext, hasPrev, hasNext])

  const copyJson = useCallback(() => {
    const payload = detail ?? record
    navigator.clipboard?.writeText(JSON.stringify(payload, null, 2)).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      },
      () => undefined
    )
  }, [detail, record])

  const action = actionMeta(record.changeType)
  const entity = entityMeta(record.entityType)
  const user = displayUser(record.userName, record.userId)

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ease-out motion-reduce:transition-none sm:p-6 ${
        entered ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop — click to close, list is no longer visible so a dim is appropriate. */}
      <div aria-hidden onClick={onClose} className="absolute inset-0 bg-black/50 motion-reduce:transition-none" />

      <section
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-detail-title"
        className={`relative flex h-full max-h-[92vh] w-full max-w-[1400px] flex-col overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-2xl outline-none transition-transform duration-200 ease-out motion-reduce:transition-none ${
          entered ? 'scale-100' : 'scale-95'
        }`}
      >
        {/* Header (pinned) */}
        <div className="shrink-0 border-b border-base-300 p-4">
          <div className="flex items-start gap-3">
            <span className="mt-1 text-lg text-base-content/70">{entity.icon}</span>
            <div className="min-w-0 flex-1">
              <h2 id="audit-detail-title" className="truncate text-lg font-semibold" title={record.entityName}>
                {record.entityName || entity.label}
              </h2>
              <div className="mt-0.5 flex items-center gap-2 text-sm text-base-content/70">
                <span>{entity.label}</span>
                <span
                  className={`inline-flex items-center gap-1 rounded-field px-2 py-0.5 text-[11px] font-semibold uppercase ${action.badgeClass}`}
                >
                  <span aria-hidden>{action.icon}</span>
                  {action.label}
                </span>
              </div>
              {outsideList && (
                <p className="mt-1 text-xs text-base-content/60">
                  Opened from a link — this record is outside your current filters.
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                aria-label="Previous record"
                disabled={!hasPrev}
                onClick={onPrev}
                className="btn btn-ghost btn-sm btn-square disabled:opacity-30"
              >
                <FiChevronUp className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Next record"
                disabled={!hasNext}
                onClick={onNext}
                className="btn btn-ghost btn-sm btn-square disabled:opacity-30"
              >
                <FiChevronDown className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Close details"
                onClick={onClose}
                className="btn btn-ghost btn-sm btn-square"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Body — two columns on wide screens so metadata stays visible while changes scroll. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          {/* Metadata rail. Comes from the list row, so it renders immediately. */}
          <aside className="shrink-0 overflow-auto border-b border-base-300 p-4 lg:w-80 lg:border-b-0 lg:border-r">
            <dl className="rounded-box border border-base-300 bg-base-100 p-3">
              <MetaRow label="When" title={formatUtc(record.loggedAt)}>
                {formatDateTime(record.loggedAt)}
              </MetaRow>
              <MetaRow label="Who" title={`User #${record.userId}`}>
                <span className={user.resolved ? '' : 'italic text-base-content/60'}>{user.text}</span>
              </MetaRow>
              <MetaRow label="Company">{displayCompany(record.companyName, record.companyId)}</MetaRow>
              <MetaRow label="IP">{record.userIpAddress || '—'}</MetaRow>
            </dl>
            <Legend />
          </aside>

          {/* Changes column (scrolls). */}
          <div className="min-h-0 flex-1 overflow-auto p-4">
            {/* Three change-row-shaped placeholders, not a spinner: the panel is already showing
                real metadata, so only the body is still unknown. */}
            {isLoading && (
              <div className="flex animate-pulse flex-col gap-3" aria-busy="true">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-[84px] rounded-box bg-base-200" />
                ))}
              </div>
            )}

            {isError && (
              <Alert
                type="error"
                message="We couldn’t display this change"
                description="The change detail failed to load."
                alertActions={
                  <Button variation="outline" size="sm" handleClick={onRetry}>
                    Try again
                  </Button>
                }
              />
            )}

            {/* A malformed diff must not produce an empty panel — the renderer degrades to the
                no-changes panel, and staff can still copy the raw JSON from the footer. */}
            {!isLoading && !isError && detail && <ChangeRenderer audit={detail} />}
          </div>
        </div>

        {/* Footer (pinned) */}
        <div className="flex shrink-0 items-center gap-2 border-t border-base-300 p-3">
          <Button variation="ghost" size="sm" icon={<FiExternalLink />} handleClick={() => onEntityHistory(record)}>
            View all changes to this entity
          </Button>
          {canCopyRawJson && (
            <Button variation="ghost" size="sm" icon={<FiCopy />} handleClick={copyJson}>
              {copied ? 'Copied!' : 'Copy raw JSON'}
            </Button>
          )}
        </div>
      </section>
    </div>
  )
}
